import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Pagination,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CommonList, ListSelectionMode } from '../common/components';
import { preOrderService } from '../../services/preOrderService';
import type { PreOrder } from '../../types/catalog';
import { PRE_ORDER_STATUS_LABELS } from './preOrderStatus';
import { formatDateTime } from '../../utils/dateTimeUtils';

/** Client-side list of the current user's own pre-orders (all statuses, newest first). */
export default function MyPreOrdersPage() {
  const navigate = useNavigate();

  const [preOrders, setPreOrders] = useState<PreOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await preOrderService.searchMine(page - 1);
        setPreOrders(response.content);
        setTotalPages(response.totalPages);
        setError(null);
      } catch (err) {
        console.error('Error fetching my pre-orders:', err);
        setError('Не вдалося завантажити передзамовлення');
      } finally {
        setLoading(false);
        setIsFirstLoad(false);
      }
    };

    fetchData();
  }, [page]);

  if (isFirstLoad) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          size="small"
        >
          На головну
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Мої передзамовлення
        </Typography>
      </Box>

      {error && (
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {preOrders.length === 0 && !loading && !error ? (
          <Box
            sx={{
              p: 6,
              textAlign: 'center',
              backgroundColor: 'background.default',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="h5" gutterBottom>
              Передзамовлень немає
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Відкрийте посилання на каталог, щоб зробити замовлення
            </Typography>
          </Box>
        ) : (
          <>
            <CommonList<PreOrder>
              items={preOrders}
              toListItem={(item) => ({
                id: item.id,
                primary: `№${item.id} · ${item.catalogName ?? 'Каталог'}`,
                secondary: `${formatDateTime(item.createdDate)} · $${item.total.toFixed(2)}`,
                tertiary: PRE_ORDER_STATUS_LABELS[item.status],
              })}
              onItemClick={(item) => navigate(`/my-preorders/${item.id}`)}
              selectionMode={ListSelectionMode.VIEW}
              showSelection={false}
              emptyMessage="Передзамовлення не знайдено"
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
